import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mountain,
  Calendar,
  Telescope,
  Menu as MenuIcon,
  Eye
} from 'lucide-react';
import { useNightVision } from './NightVisionContext';

interface BottomNavigationProps {
  onMoreClick: () => void;
  isNative?: boolean;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({ onMoreClick, isNative }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isNightVision, toggleNightVision } = useNightVision();

  if (!isNative) return null;

  const navItems = [
    {
      to: '/horizon-generator',
      label: t('planner.mobile.nav.horizon'),
      icon: Mountain,
    },
    {
      to: '/planner',
      label: t('planner.mobile.nav.planner'),
      icon: Calendar,
      isSpecial: true,
    },
    {
      // [US 6.9 inc.2] Sentinel : le bouton ouvre désormais le rapport du soir (dashboard).
      to: '/sentinel/tonight',
      label: t('planner.mobile.nav.sentinel'),
      icon: Telescope,
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0d1117]/80 backdrop-blur-xl border-t border-white/10 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.5)]">
      {/* Bouton Vision Nocturne flottant au-dessus de la barre */}
      <button
        onClick={toggleNightVision}
        className={`absolute -top-12 right-3 z-[61] w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border shadow-lg
          ${isNightVision
            ? 'bg-red-900/80 border-red-500/60 text-red-300 shadow-[0_0_12px_rgba(220,38,38,0.4)]'
            : 'bg-zinc-900/70 border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
          }`}
        title={isNightVision ? t('nightVision.enabled') : t('nightVision.disabled')}
      >
        <Eye size={18} strokeWidth={isNightVision ? 2.5 : 2} />
        {isNightVision && (
          <span className="absolute inset-0 rounded-full animate-ping bg-red-500/20" />
        )}
      </button>

      {/* Navigation items container */}
      <div className="px-2 py-1">
        <nav className="flex items-center justify-around max-w-lg mx-auto h-16">
          {navItems.map((item) => {
            const isActive = location.pathname === item.to;
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `
                  relative flex flex-col items-center justify-center w-full h-full gap-1 transition-all duration-300
                  ${isActive ? 'text-primary' : 'text-zinc-500 hover:text-zinc-300'}
                `}
              >
                <div className={`p-1 rounded-lg transition-colors ${isActive ? 'bg-primary/10' : ''}`}>
                  <Icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  {item.label}
                </span>
                
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -top-1 w-8 h-1 bg-primary rounded-full shadow-[0_0_8px_rgba(var(--primary-rgb),0.5)]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </NavLink>
            );
          })}

          {/* Bouton "More" pour le menu complet */}
          <button
            onClick={onMoreClick}
            className="flex flex-col items-center justify-center w-full h-full gap-1 text-zinc-500 hover:text-zinc-300 transition-all"
          >
            <div className="p-1">
              <MenuIcon size={24} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {t('planner.mobile.nav.more')}
            </span>
          </button>
        </nav>
      </div>
    </div>
  );
};

